/**
 * The `<ul>` under a run of thread rows, in the one shape each layout needs.
 *
 * Projects nests its rows under a project row, so it takes `SidebarMenuSub` and
 * with it the tree rail — a left hairline and an indent that say the rows below
 * belong to the row above. Activity has no row above: its cards are the list,
 * and each one names its own project, so the rail there is a line down the
 * sidebar attached to nothing and the indent is a margin with no parent to be
 * indented from. t3code lists these cards as a plain `ul` with a 1px gap, which
 * is what `isFlat` renders.
 *
 * The flat branch is a different element rather than `SidebarMenuSub` with the
 * rail overridden off. The rail lives in that primitive's base classes, so an
 * override is a standing bet that nobody edits them — and the call site here
 * was already overriding two of those base classes without noticing the
 * `border-l` underneath. A plain `ul` cannot grow a rail back.
 *
 * Both branches are `<ul>` elements, so `SidebarMenuSubItem` stays the row's
 * `<li>` on either layout — the card's status/action slot cross-fades off
 * `group/menu-sub-item`, which that primitive owns.
 *
 * The flat branch also owns the list's tooltip timing. See
 * `ACTIVITY_TOOLTIP_TIMING` below.
 */

import { SidebarMenuSub } from "../ui/sidebar";
import { TooltipProvider } from "../ui/tooltip";

/**
 * t3code's sidebar tooltip timing, which is a property of the *list* rather
 * than of any one card.
 *
 * Base UI's defaults (600ms to open, no shared group) make the Activity card's
 * details panel feel like a thing you have to wait out: two fifths of a second
 * later than upstream, and the full delay charged again on every neighbouring
 * card you move onto. `timeout` is the part that does the most work — it is the
 * window after one tooltip closes during which the next opens instantly, so
 * scanning down the list reads as one panel following the cursor instead of a
 * dozen independent waits.
 *
 * Scoped to the flat list on purpose. Projects nests its rows under project
 * rows whose own tooltips are labels, not detail panels, and 150ms on a label
 * is a flicker.
 */
const ACTIVITY_TOOLTIP_TIMING = { delay: 150, closeDelay: 0, timeout: 400 } as const;

export function ThreadListShell(props: {
  readonly isFlat: boolean;
  readonly children: React.ReactNode;
}) {
  if (props.isFlat) {
    return (
      <TooltipProvider {...ACTIVITY_TOOLTIP_TIMING}>
        <ul data-activity-thread-list className="flex min-w-0 list-none flex-col gap-px">
          {props.children}
        </ul>
      </TooltipProvider>
    );
  }
  return (
    <SidebarMenuSub className="mx-1.5 mt-0.5 gap-0.5 border-border/20 px-2 py-0">
      {props.children}
    </SidebarMenuSub>
  );
}
